import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import ProjectProposalForm from "../../../components/project/ProjectProposalForm/ProjectProposalForm";
import ProjectVettingState from "../../../components/project/ProjectVettingState/ProjectVettingState";
import ProjectRequirementsDashboardContainer from "../ProjectRequirementsDashboardContainer/ProjectRequirementsDashboardContainer";
import SubmitProject from "../../../components/project/SubmitProject/SubmitProject";
import ProjectDetailsModal from "../../../components/project/ProjectDetailsModal/ProjectDetailsModal";
import {
  useGetCourseByIdQuery,
  useGetProjectFrameworkQuery,
  useGetProjectSubmissionsByProjectQuery,
  useCreateProjectSubmissionMutation,
  useSubmitFinalProjectMutation,
  ProjectSubmissionStatus,
  ProjectVettingStatus,
  CreateProjectSubmissionInput,
  useUpdateProjectSubmissionMutation,
  useDeleteProjectSubmissionMutation,
  SubmitFinalProjectInput,
} from "../../../graphql/generated/graphql";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { SettingUpProjectWorkspaceModal } from "../../../components/project/SettingUpProjectWorkspaceModal/SettingUpProjectWorkspaceModal";
import ProjectDeleteModal from "../../../components/project/ProjectDeleteModal/ProjectDeleteModal";
import { Trash, ArrowLeft } from "@phosphor-icons/react";
import { ScrollView } from "react-native-gesture-handler";

const ProjectWorkflowContainer = () => {
  const [currentStep, setCurrentStep] = useState("proposal");

  const [userRequestedRevision, setUserRequestedRevision] = useState(true); // Track if user explicitly requested revision
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const route = useRoute<RouteProp<RootStackParamList, "Project">>();
  const { id: projectId, courseId, integrationId, initialStep } = route.params;

  const [resetVettingState, setResetVettingState] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  const handleClose = () => {
    // Navigate back to the appropriate view
    if (courseId && integrationId) {
      // If we came from a course, go back to that course
      navigation.navigate("Custom Course Student", {
        courseId: courseId,
        integration_id: integrationId,
      });
    } else {
      // Otherwise go back to dashboard
      navigation.navigate("Home");
    }
  };

  // GraphQL queries and mutations
  const { data: courseData, loading: courseLoading } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
      withModules: false,
    },
    skip: !courseId,
  });

  // Get detailed project data using dedicated project query
  const {
    data: projectData,
    loading: projectLoading,
    refetch: refetchProject,
  } = useGetProjectFrameworkQuery({
    variables: { id: projectId },
    skip: !projectId,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.getProject?.data) {
        // Only handle the creating -> dashboard transition here
        // All other step determination is handled by the coordination useEffect
        if (
          currentStep === "creating" &&
          submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]?.totalTasks > 0
        ) {
          setCurrentStep("dashboard");
          stopPolling();
        }
      }
    },
  });

  const {
    data: submissionDataQuery,
    loading: submissionLoading,
    startPolling,
    stopPolling,
    refetch: refetchSubmission,
  } = useGetProjectSubmissionsByProjectQuery({
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: "no-cache",
  });

  const [createSubmission, { loading: proposalLoading }] = useCreateProjectSubmissionMutation({
    refetchQueries: ["GetProjectSubmissionsByProject"],
    onCompleted: async (data) => {
      if (data?.createProjectSubmission?.data) {
        setCurrentStep("vetting");
      }
    },
  });

  const [updateSubmission, { loading: updateLoading }] = useUpdateProjectSubmissionMutation({
    refetchQueries: ["GetProjectSubmissionsByProject"],
    onCompleted: async (data) => {
      if (data?.updateProjectSubmission?.data) {
        setCurrentStep("vetting");
      }
    },
  });

  const [submitFinalProject, { loading: finalSubmissionLoading }] = useSubmitFinalProjectMutation({
    refetchQueries: ["GetProject", "GetProjectFramework", "GetProjectSubmissionsByProject"],
  });

  const handleProposalSubmit = async (formData: CreateProjectSubmissionInput) => {
    // Reset the revision flag since user is submitting
    setUserRequestedRevision(false);

    // Check if we have an existing submission to update
    if (
      submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0] &&
      submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].id
    ) {
      await updateSubmission({
        variables: {
          id: submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].id,
          submission: {
            proposal: formData.proposal,
            approachMethodology: formData.approachMethodology,
            scopeJustification: formData.scopeJustification,
            expectedOutcomes: formData.expectedOutcomes,
          },
        },
      });
    } else {
      await createSubmission({
        variables: {
          submission: {
            projectId: formData.projectId || projectId,
            proposal: formData.proposal,
            approachMethodology: formData.approachMethodology,
            scopeJustification: formData.scopeJustification,
            expectedOutcomes: formData.expectedOutcomes,
          },
        },
      });
    }

    setResetVettingState(true);
  };

  const handleVettingAccept = async () => {
    if (
      submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].status === ProjectSubmissionStatus.Submitted ||
      submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].vettingStatus === ProjectVettingStatus.Approved
    ) {
      // Submission is approved, move to task generation screen first

      setCurrentStep("creating");

      // Start polling both submission and project data to check when tasks are generated
      startPolling(3000); // Poll every 3 seconds for task generation

      // Also set up interval to refetch project data to check for task generation completion
      const projectPollInterval = setInterval(async () => {
        try {
          await refetchSubmission();

          if (submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]?.totalTasks > 0) {
            setCurrentStep("dashboard");
            stopPolling();
            clearInterval(projectPollInterval);
          }
        } catch (error) {}
      }, 3000);

      // Clean up interval after 5 minutes (fallback)
      setTimeout(() => {
        clearInterval(projectPollInterval);
      }, 300000);
    }
  };

  const handleVettingRevise = () => {
    setUserRequestedRevision(true); // Flag that user explicitly requested revision

    setCurrentStep("proposal");
  };

  const handleViewDetails = () => {
    setShowProjectDetailsModal(true);
  };

  const handleSubmitProject = () => {
    setShowSubmitModal(true);
  };

  const handleCloseSubmitModal = () => {
    setShowSubmitModal(false);
  };

  const handleProjectSubmission = async (submissionFormData: SubmitFinalProjectInput) => {
    try {
      await submitFinalProject({
        variables: {
          submission: {
            projectId: submissionFormData.projectId || projectId,
            submissionLink: submissionFormData.submissionLink,
            submissionNotes: submissionFormData.submissionNotes,
            submissionFiles: submissionFormData.submissionFiles,
          },
        },
      });
      setShowSubmitModal(false);
      setCurrentStep("dashboard");
    } catch (error) {
      console.error("Error submitting project:", error);
      showToast({
        message: "Failed to submit project. Please try again.",
        type: "error",
      });
    }
  };

  const handleBackToRequirements = () => {
    // Go back to requirements dashboard
    setCurrentStep("dashboard");
  };

  const handleProjectDeleted = () => {
    handleClose();
    setShowDeleteModal(false);
  };

  const [deleteProjectSubmission, { loading: deletingProjectSubmission }] = useDeleteProjectSubmissionMutation({
    onCompleted: (data) => {
      if (data.deleteProjectSubmission.success) {
        handleProjectDeleted();
      } else {
        showToast({
          message: data.deleteProjectSubmission.message || "Failed to delete project",
          type: "error",
        });
      }
    },
    onError: (error) => {
      console.error("Error deleting project:", error);
      showToast({
        message: "Failed to delete project. Please try again.",
        type: "error",
      });
    },
  });

  // Handle deleting project
  const handleDeleteProject = async () => {
    try {
      await deleteProjectSubmission({
        variables: { submissionId: submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]?.id },
      });
    } catch (error) {
      console.error("Error deleting project:", error);
      showToast({
        message: "Failed to delete project. Please try again.",
        type: "error",
      });
    }
  };

  const courseInfo = courseData?.getCourseById?.data?.[0] || null;

  // State coordination effect - handles proper step determination when data loads
  useEffect(() => {
    const determineCorrectStep = () => {
      // Don't interfere if we're actively creating tasks or in submit mode
      if (currentStep === "creating" || currentStep === "submit") {
        return;
      }

      // Don't interfere if user explicitly requested revision and we're on proposal step
      if (userRequestedRevision && currentStep === "proposal") {
        return;
      }

      // If we don't have submission data yet, wait
      if (!submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]) {
        return;
      }

      // If we don't have project framework data yet, wait
      if (!projectData?.getProject?.data) {
        return;
      }

      const vettingComplete =
        submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].complianceScore !== null &&
        submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].complianceScore !== undefined;
      const tasksGenerated = submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].totalTasks > 0;
      const projectApproved =
        vettingComplete &&
        (submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].status === ProjectSubmissionStatus.Approved ||
          submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].vettingStatus ===
            ProjectVettingStatus.Approved);
      const projectCompleted =
        submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].status === ProjectSubmissionStatus.Completed &&
        submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].isCompleted;

      // If project is completed (deliverables submitted), stay on dashboard but in completed state
      if (projectCompleted && tasksGenerated) {
        if (currentStep !== "dashboard") {
          setCurrentStep("dashboard");
        }
        stopPolling();
        return;
      }

      // If project is approved and tasks are generated, go to dashboard
      if (projectApproved && tasksGenerated) {
        setCurrentStep("dashboard");
        stopPolling();

        return;
      }

      // If project is approved but no tasks yet, stay on vetting for user to accept
      if (projectApproved && !tasksGenerated) {
        if (currentStep !== "vetting") {
          setCurrentStep("vetting");
        }
        return;
      }

      // If vetting is in progress, show vetting screen (unless user requested revision)
      if (
        submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].status === ProjectSubmissionStatus.Submitted &&
        !vettingComplete &&
        !userRequestedRevision
      ) {
        if (currentStep !== "vetting") {
          setCurrentStep("vetting");
        }
        return;
      }

      // If submission is draft, show proposal form
      if (submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0].status === ProjectSubmissionStatus.Draft) {
        if (currentStep !== "proposal") {
          setCurrentStep("proposal");
        }
        return;
      }

      // Default fallback - if we have complete vetting, show vetting screen (unless user requested revision)
      if (vettingComplete && !userRequestedRevision) {
        if (currentStep !== "vetting") {
          setCurrentStep("vetting");
        }
        return;
      }
    };

    // Run determination logic when data changes
    determineCorrectStep();
  }, [submissionDataQuery, projectData, currentStep, stopPolling, userRequestedRevision]);

  const isLoading =
    proposalLoading || courseLoading || projectLoading || submissionLoading || updateLoading || finalSubmissionLoading;

  // Render based on current step
  const renderMainContent = () => {
    switch (currentStep) {
      case "proposal":
        return (
          <ProjectProposalForm
            courseInfo={courseInfo}
            projectFramework={projectData?.getProject?.data}
            onSubmit={handleProposalSubmit}
            loading={isLoading}
            submissionData={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
          />
        );
      case "creating":
      case "vetting":
        return (
          <ProjectVettingState
            onAccept={handleVettingAccept}
            onRevise={handleVettingRevise}
            resetVettingState={resetVettingState}
            setResetVettingState={setResetVettingState}
            submission={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
            submissionRefetch={refetchSubmission}
            projectFramework={projectData?.getProject?.data}
          />
        );

      case "dashboard":
        return (
          <ProjectRequirementsDashboardContainer
            submission={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
            refetchSubmission={refetchSubmission}
          />
        );

      default:
        return (
          <ProjectProposalForm
            courseInfo={courseInfo}
            projectFramework={projectData?.getProject?.data}
            onSubmit={handleProposalSubmit}
            loading={isLoading}
            submissionData={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
          />
        );
    }
  };

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const moduleId = courseInfo.moduleOrder?.[0];
      if (moduleId && typeof moduleId === "string") {
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: courseId,
          integration_id: integrationId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          courseId: courseId,
          integration_id: integrationId,
        });
      }
    }
  };

  const handleOnTasksReady = useCallback(() => {
    setCurrentStep("dashboard");
    stopPolling();
  }, [stopPolling]);

  const canSubmitProject = () => {
    const status = submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]?.status;
    return status === ProjectSubmissionStatus.Approved;
  };

  const isProjectCompleted = () => {
    const submission = submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0];
    return submission?.status === ProjectSubmissionStatus.Completed && submission?.isCompleted === true;
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View style={styles.exitButtonContainer}>
            <CustomButton
              leftIcon={<ArrowLeft />}
              onPress={handleExitPress}
              size="s"
              styleType="transparent"
              text="Exit"
              rightIcon={null}
              style={{}}
              textStyle={{}}
              disabled={false}
              textProps={{}}
            />
          </View>
          {/* Display the course name */}
          <CustomSpacing type="horizontal" size="m" />
          <CustomText
            text={`Submit Your Project Proposal${courseInfo ? ` - ${courseInfo.name || "Unknown Course"}` : ""}`}
            useTranslationText={false}
            weight="bold"
            size="m"
          />
        </View>
        <View style={styles.headerRight}>
          {/* Delete Project Button */}
          {!isProjectCompleted() && currentStep === "dashboard" && (
            <CustomButton
              text="Delete"
              size="s"
              styleType="danger"
              onPress={() => setShowDeleteModal(true)}
              style={styles.deleteButton}
              icon={<Trash size={16} color="#ffffff" />}
            />
          )}
          {currentStep === "dashboard" && (
            <CustomButton
              text="Project Framework"
              size="s"
              styleType="primaryLight"
              onPress={handleViewDetails}
              style={styles.detailsHeaderButton}
            />
          )}
          {isProjectCompleted() ? (
            <CustomButton
              text="Project Submitted"
              size="s"
              onPress={() => {}}
              disabled={true}
              style={styles.submittedHeaderButton}
              textStyle={styles.submittedHeaderButtonText}
            />
          ) : (
            canSubmitProject() && (
              <CustomButton
                text="Submit Project"
                size="s"
                styleType="primary"
                onPress={handleSubmitProject}
                style={styles.submitHeaderButton}
              />
            )
          )}
        </View>
      </View>
      {renderMainContent()}
      <SettingUpProjectWorkspaceModal
        open={currentStep === "creating"}
        submission={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
        onTasksReady={handleOnTasksReady}
      />

      {/* Delete Project Modal */}
      {submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]?.project != null && (
        <ProjectDeleteModal
          visible={showDeleteModal}
          project={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]?.project}
          onConfirm={handleDeleteProject}
          onClose={() => setShowDeleteModal(false)}
          loading={deletingProjectSubmission}
        />
      )}

      {/* Project Details Modal */}
      <ProjectDetailsModal
        visible={showProjectDetailsModal}
        project={projectData?.getProject?.data}
        submission={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
        onClose={() => setShowProjectDetailsModal(false)}
      />

      {/* Submit Project Modal */}
      <CustomModal
        visible={showSubmitModal}
        onRequestClose={handleCloseSubmitModal}
        title="Submit Your Project"
        showCloseButton={true}
        ariaLabel="Submit project modal">
        <CustomText
          text="Once you submit, your project is final and can't be edited. Make sure everything is ready before turning it in."
          size="s"
          style={styles.modalDescription}
          useTranslationText={false}
        />
        <CustomSpacing type="vertical" size="m" />
        <SubmitProject
          project={projectData?.getProject?.data}
          onSubmitProject={handleProjectSubmission}
          onBackToRequirements={handleCloseSubmitModal}
          loading={isLoading}
          submissionData={submissionDataQuery?.getProjectSubmissionsByProject?.data?.[0]}
        />
      </CustomModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: "#D0D5DD",
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  headerRight: {
    paddingRight: 20,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  exitButtonContainer: {
    borderRightColor: "#D0D5DD",
    borderRightWidth: 1,
    paddingHorizontal: 10,
  },
  deleteButton: {
    fontWeight: 700,
    backgroundColor: "#F04438",
    minWidth: 32,
    paddingHorizontal: 8,
  },
  submitHeaderButton: {
    fontWeight: 700,
    backgroundColor: "#3E68FE",
  },
  submittedHeaderButton: {
    backgroundColor: "#EAECF0",
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  submittedHeaderButtonText: {
    color: "#475467",
    fontWeight: "700",
  },
  detailsHeaderButton: {
    fontWeight: 700,
    borderColor: "#05060F1A",
    borderWidth: 1,
  },
  modalDescription: {
    color: "#667085",
    lineHeight: 20,
  },
});

export default ProjectWorkflowContainer;
